import { useTranslation } from 'next-i18next';
import { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { LoadingCircle } from '@bridebook/ui';
import {
  Button,
  cn,
  Flex,
  FullPageModal,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ResponsiveModalContent,
  ResponsiveModalHeader,
} from '@bridebook/aura';
import { useSelector } from '@bridebook/web/lib/utils';
import { ChevronDown, Copy, Pencil, MessageLeft, Check } from '@bridebook/aura/src/icons';
import { CopyToClipboardButton } from '@bridebook/web/components/wedding-website/main/desktop/components/copy-to-clipboard-button';
import { MobileDesktopToggle } from '@bridebook/web/components/wedding-website/main/desktop/components/mobile-desktop-toggle';
import { WebsitePreviewWrapper } from '@bridebook/web/components/wedding-website/main/desktop/components/website-preview';
import {
  getWebsiteDirty,
  getWebsiteId,
  getWebsiteSections,
} from '@bridebook/web/lib/weddings/selectors';
import { useWeddingWebsite } from '@bridebook/web/lib/wedding-website/hooks/query';
import { getIsWeddingWebsitePublished } from '@bridebook/web/lib/wedding-website/hooks/query/selectors';
import { useShareWeddingWebsite } from '@bridebook/web/lib/wedding-website/hooks/use-share-wedding-website';
import { useMessageGuestsModal } from '@bridebook/web/components/inbox/guests/guests-messages-container';
import { useCopyToClipboardWithFeedback } from '@bridebook/web/lib/utils/hooks/use-copy-to-clipboard-with-feedback';
import { useTimeout } from '@bridebook/web/lib/utils/hooks/use-timeout';
import {
  ShareWebsiteCta,
  useShareWeddingWebsiteClick,
} from '@bridebook/web/lib/wedding-website/analytics/use-share-wedding-website-click';
import {
  buildSectionsOrderDict,
  isDefaultSectionsOrder,
} from '@bridebook/web/lib/wedding-website/analytics/utils';
import { THEME_BRIDEBOOK } from '@bridebook/web/lib/wedding-website/constants/themes';
import { DesktopPreviewDevice } from '@bridebook/web/lib/wedding-website/desktop/types';
import GuestRsvpMainComponent from './guest-rsvp-main-component';
import {
  NOOP_RSVP_ANALYTICS,
  RsvpAnalyticsProvider,
  RsvpDataProvider,
  RsvpFormsProvider,
  RsvpNavigationProvider,
  RsvpStateProvider,
} from './guest-rsvp.context';
import { RsvpPreviewData, useRsvpPreviewData } from './use-rsvp-preview-data';
import { useRsvpPreviewForms } from './use-rsvp-preview-forms';
import { TGuestRSVPStep, TGuestRsvpSubmissionStatus } from '@bridebook/web/lib/rsvp/types';

interface GuestRsvpPreviewProps {
  onClose: () => void;
  variant: 'mobile' | 'desktop';
}

/**
 * Desktop: full-page preview matching FullPagePreviewModal layout with
 * "Back to editor", URL, mobile/desktop toggle, and Share dropdown.
 */
const PreviewSharePopover = ({ disabled }: { disabled: boolean }) => {
  const { t } = useTranslation('weddingWebsite');
  const { isCopied, copy } = useCopyToClipboardWithFeedback();
  const { websiteUrl } = useShareWeddingWebsite({ linkToRsvp: true });
  const { openModal: openMessageGuestsModal, isDisabled: isMessageGuestsDisabled } =
    useMessageGuestsModal();
  const sections = useSelector(getWebsiteSections);
  const { trackShareWeddingWebsiteClick } = useShareWeddingWebsiteClick();
  const [open, setOpen] = useState(false);
  const { schedule } = useTimeout();

  const handleClickAnalytics = useCallback(
    (cta: ShareWebsiteCta) => {
      trackShareWeddingWebsiteClick({
        category: 'weddingWebsites',
        clickedLocation: 'weddingWebsites_preview',
        cta,
        sectionsOrderDict: buildSectionsOrderDict(sections),
        defaultOrderingFlag: isDefaultSectionsOrder(sections),
      });
    },
    [sections, trackShareWeddingWebsiteClick],
  );

  const handleCopy = useCallback(() => {
    copy(websiteUrl);
    handleClickAnalytics('Copy link');
    schedule(() => setOpen(false), 300);
  }, [copy, websiteUrl, handleClickAnalytics, schedule]);

  const handleMessageGuests = useCallback(() => {
    handleClickAnalytics('Message guests');
    setOpen(false);
    openMessageGuestsModal();
  }, [handleClickAnalytics, openMessageGuestsModal]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="primary"
          size="sm"
          iconRight={ChevronDown}
          disabled={disabled}
          onClick={() => handleClickAnalytics('Share')}>
          {t('websiteAction.share')}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="min-w-[200px] rounded-xl bg-white p-2 shadow-lg">
        <button
          onClick={handleCopy}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-space-04">
          <Icon icon={isCopied ? Check : Copy} size={24} className="text-space" />
          <span>{t('websiteAction.copyLink')}</span>
        </button>
        <button
          onClick={isMessageGuestsDisabled ? undefined : handleMessageGuests}
          disabled={isMessageGuestsDisabled}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left',
            isMessageGuestsDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-space-04',
          )}>
          <Icon
            icon={MessageLeft}
            size={24}
            className={isMessageGuestsDisabled ? 'text-space-40' : 'text-space'}
          />
          <span>{t('websiteAction.messageGuests')}</span>
        </button>
      </PopoverContent>
    </Popover>
  );
};

const DesktopPreviewLayout = ({
  onClose,
  children,
}: PropsWithChildren<{ onClose: () => void }>) => {
  const { t } = useTranslation('weddingWebsite');
  const { websiteUrl } = useShareWeddingWebsite({ linkToRsvp: true });
  const { data: isPublished } = useWeddingWebsite({ select: getIsWeddingWebsitePublished });
  const isDirty = useSelector(getWebsiteDirty);
  const [activePreviewDevice, setActivePreviewDevice] = useState<DesktopPreviewDevice>(
    DesktopPreviewDevice.Desktop,
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) onClose();
    },
    [onClose],
  );

  return (
    <FullPageModal isOpen onOpenChange={handleOpenChange} aria-label={t('rsvp.preview.title')}>
      {/* Header — mirrors FullPagePreviewModal */}
      <Flex className="relative justify-between border-b border-space-15 p-6">
        <Flex className="items-center gap-6">
          <Button variant="tertiary" size="sm" icon={Pencil} onClick={onClose}>
            {t('desktop.fullPagePreview.backToEditor')}
          </Button>
          <Flex className="items-center gap-1">
            <div className="text-indigo-crush">{websiteUrl}</div>
            <CopyToClipboardButton text={websiteUrl} disabled={isDirty} />
          </Flex>
        </Flex>
        <Flex className="items-center gap-4">
          <MobileDesktopToggle
            activePreviewDevice={activePreviewDevice}
            setActivePreviewDevice={setActivePreviewDevice}
          />
          <PreviewSharePopover disabled={!isPublished || isDirty} />
        </Flex>
      </Flex>

      {/* Content — WebsitePreviewWrapper provides phone frame for mobile toggle */}
      <Flex
        className={cn(
          'min-h-0 flex-1 flex-col overflow-y-hidden bg-space-04',
          activePreviewDevice === DesktopPreviewDevice.Mobile && 'py-10',
        )}>
        <WebsitePreviewWrapper activePreviewDevice={activePreviewDevice}>
          {children}
        </WebsitePreviewWrapper>
      </Flex>
    </FullPageModal>
  );
};

const LoadingContent = () => (
  <Flex className="flex-1 items-center justify-center py-20">
    <LoadingCircle size={40} color="indigoCrush" />
  </Flex>
);

interface RsvpPreviewFormProps {
  onClose: () => void;
  websiteId: string;
  previewData: RsvpPreviewData;
}

const RsvpPreviewForm = ({ onClose, websiteId, previewData }: RsvpPreviewFormProps) => {
  const { weddingRsvp, weddingWebsiteData, guestInvitation, enrichedEvents } = previewData;

  const [step, setStep] = useState<TGuestRSVPStep>('RSVP_FORM.EVENTS');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [rsvpSubmissionStatus, setRsvpSubmissionStatus] =
    useState<TGuestRsvpSubmissionStatus>('FIRST_SUBMISSION');

  const { nameForm, eventsForm, questionsForm, handleSubmit } = useRsvpPreviewForms({
    visibleEvents: weddingWebsiteData.events,
    weddingRsvp,
    websiteId,
  });

  const rsvpStateValue = useMemo(
    () => ({
      websiteId,
      isPreview: true,
      isRsvpOpen: true,
      rsvpSubmissionStatus,
      setRsvpSubmissionStatus,
    }),
    [websiteId, rsvpSubmissionStatus],
  );

  const navigationValue = useMemo(
    () => ({
      step,
      setStep,
      currentQuestionIndex,
      setCurrentQuestionIndex,
      onClose,
    }),
    [step, currentQuestionIndex, onClose],
  );

  const formsValue = useMemo(
    () => ({
      nameForm,
      eventsForm,
      questionsForm,
      handleSubmit,
    }),
    [nameForm, eventsForm, questionsForm, handleSubmit],
  );

  const dataValue = useMemo(
    () => ({
      isLoading: false as const,
      error: null,
      guestNotFound: false as const,
      guestNoEvents: false as const,
      guestInvitation,
      weddingRsvp: weddingRsvp || undefined,
      weddingWebsite: weddingWebsiteData,
      theme: THEME_BRIDEBOOK,
      enrichedEvents,
      relatedGuests: [],
    }),
    [guestInvitation, weddingRsvp, weddingWebsiteData, enrichedEvents],
  );

  return (
    <RsvpStateProvider value={rsvpStateValue}>
      <RsvpNavigationProvider value={navigationValue}>
        <RsvpFormsProvider value={formsValue}>
          <RsvpDataProvider value={dataValue}>
            <RsvpAnalyticsProvider value={NOOP_RSVP_ANALYTICS}>
              <GuestRsvpMainComponent />
            </RsvpAnalyticsProvider>
          </RsvpDataProvider>
        </RsvpFormsProvider>
      </RsvpNavigationProvider>
    </RsvpStateProvider>
  );
};

const MobilePreviewHeader = () => {
  const { t } = useTranslation('weddingWebsite');
  return <ResponsiveModalHeader>{t('rsvp.preview.title')}</ResponsiveModalHeader>;
};

const GuestRsvpPreview = ({ onClose, variant }: GuestRsvpPreviewProps) => {
  const websiteId = useSelector(getWebsiteId);
  const previewData = useRsvpPreviewData();

  if (!websiteId) return null;

  if (previewData.isLoading) {
    if (variant === 'desktop') {
      return (
        <DesktopPreviewLayout onClose={onClose}>
          <LoadingContent />
        </DesktopPreviewLayout>
      );
    }
    return <LoadingContent />;
  }

  if (variant === 'desktop') {
    return (
      <DesktopPreviewLayout onClose={onClose}>
        <RsvpPreviewForm onClose={onClose} websiteId={websiteId} previewData={previewData} />
      </DesktopPreviewLayout>
    );
  }

  return (
    <>
      <MobilePreviewHeader />
      <ResponsiveModalContent className="!p-0">
        <RsvpPreviewForm onClose={onClose} websiteId={websiteId} previewData={previewData} />
      </ResponsiveModalContent>
    </>
  );
};

export default GuestRsvpPreview;
